import type { RISLinkMetadata } from '../contexts/RISearchContext';
import { sanitizeHtml } from './sanitize';
import { convertMarkdownTables, restoreTablePlaceholders } from './markdownTableParser';
import { documentMarkerPartsPattern } from '../services/documentMarker';
import { linkResolvedDocumentUrls, type ResolvedDocumentMap } from '../services/documentSourceResolver';

// F10-26a (Korrekturlauf 09.10.2026): die Darstellung einer Antwort der Recherche (Überschriften, Listen, Quellen als
// Links), herausgelöst aus pages/RISAssistant.tsx, damit eine geteilte Momentaufnahme sie liest wie der Chat
// (components/SharedResearchPanel.tsx). Es ist dieselbe Formatierung wie im Chat (formatMessageHtml, samt der
// gemeinsamen Dokumentmarke); eine Momentaufnahme hat nur keine aufgelösten Dokumentadressen.
// antwortRohHtml liefert das HTML vor der Bereinigung und braucht kein DOM (Tests). In die Seite kommt nur antwortHtml
// bzw. nachrichtHtml: dort ist sanitizeHtml der letzte Schritt.

/** Die gemeinsame Dokumentmarke, zerlegt in Kennung, Titel und Adresse. */
const DOCUMENT_MARKER_PARTS = documentMarkerPartsPattern();

// Helper to create RIS link HTML with data attributes for in-app viewer
const escHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const createRISLinkHtml = (url: string, text: string, className: string = 'text-primary hover:text-accent-dark underline font-medium') => {
  const safeUrl = escHtml(url);
  const safeText = escHtml(text);
  // Check if this is a RIS URL that should open in the viewer
  const isRISUrl = url.includes('ris.bka.gv.at');
  if (isRISUrl) {
    return `<a href="${safeUrl}" data-ris-url="${safeUrl}" class="${className} cursor-pointer">${safeText}</a>`;
  }
  return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer" class="${className}">${safeText}</a>`;
};

/**
 * Die schmale HTML-Formatierung der eigenen Nachricht -- rohes HTML, noch ohne
 * Bereinigung. Jeder übernommene Text wird hier selbst maskiert (escHtml); die
 * Bereinigung danach ist die zweite Schranke, nicht die erste. Exportiert,
 * damit die Maskierung ohne die Bereinigung geprüft werden kann.
 */
export function formatMessageHtml(
  content: string,
  resolvedDocuments: ResolvedDocumentMap,
  risLinkMetadata?: RISLinkMetadata,
): string {
  // Step 1: Extract markdown tables and replace with placeholders
  const { content: contentWithPlaceholders, tables } = convertMarkdownTables(content);

  // Step 1b: Adressen, die auf ein Dokument des eigenen Korpus aufgelöst
  // wurden, werden vor jeder anderen Adressbehandlung zu Viewer-Verweisen.
  const contentWithDocumentLinks = linkResolvedDocumentUrls(contentWithPlaceholders, resolvedDocuments);

  // Step 2: Enhanced formatting with URL handling, word breaking, and Markdown headers
  let formatted = contentWithDocumentLinks
    // Convert Markdown headers to HTML (#### -> h4, ### -> h3, ## -> h2, # -> h1)
    .replace(/^#### (.*$)/gm, '<h4 class="text-base font-semibold text-gray-900 dark:text-gray-100 mt-3 mb-1">$1</h4>')
    .replace(/^### (.*$)/gm, '<h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-4 mb-2">$1</h3>')
    .replace(/^## (.*$)/gm, '<h2 class="text-xl font-bold text-gray-900 dark:text-gray-100 mt-6 mb-3">$1</h2>')
    .replace(/^# (.*$)/gm, '<h1 class="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-8 mb-4">$1</h1>')
    // Convert lines starting with * to bullet points
    .replace(/^\* (.*)$/gm, '<li class="ml-4 list-disc list-inside">$1</li>')
    // Convert numbered lists (1. item, 2. item)
    .replace(/^(\d+)\.\s+(.*)$/gm, '<li class="ml-4 list-decimal list-inside" value="$1">$2</li>')
    // Convert three dashes to divider
    .replace(/^---\s*\n*/gm, '<hr class="mt-0 mb-6 border-t border-gray-300">')
    // Bold and italic formatting
    .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>')
    .replace(/\*(.*?)\*/g, '<em class="italic">$1</em>')
    // Convert paragraph references with URLs to clean clickable links
    .replace(/(\u00A7\s*\d+[a-zA-Z]*(?:\s+(?:Abs\.?\s*\d+|[A-Za-z0-9]+))+)\s*\((https?:\/\/[^\s)]+)\)/g,
      (_, text, url) => createRISLinkHtml(url, text))
    // Also handle square brackets for paragraph references
    .replace(/(\u00A7\s*\d+[a-zA-Z]*(?:\s+(?:Abs\.?\s*\d+|[A-Za-z0-9]+))+)\s*\[(https?:\/\/[^\s\]]+)\]/g,
      (_, text, url) => createRISLinkHtml(url, text))
    // Convert Rechtssatz references with URLs to clickable links
    .replace(/\b(RS\d+)\s*\((https?:\/\/[^\s)]+)\)/g,
      (_, text, url) => createRISLinkHtml(url, text))
    .replace(/\b(RS\d+)\s*\[(https?:\/\/[^\s\]]+)\]/g,
      (_, text, url) => createRISLinkHtml(url, text))
    // Convert court decision references with URLs
    .replace(/\b((?:OGH|VfGH|VwGH|BVwG|LVwG|OLG)[^\s()\[\]<>]*(?:\s+[^\s()\[\]<>]+)*)\s*\((https?:\/\/[^\s)]+)\)/g,
      (_, text, url) => createRISLinkHtml(url, text))
    .replace(/\b((?:OGH|VfGH|VwGH|BVwG|LVwG|OLG)[^\s()\[\]<>]*(?:\s+[^\s()\[\]<>]+)*)\s*\[(https?:\/\/[^\s\]]+)\]/g,
      (_, text, url) => createRISLinkHtml(url, text))
    // ELI-Links
    .replace(/\((https?:\/\/(?:www\.)?ris\.bka\.gv\.at\/eli\/[^\/]+\/[^\/]+\/\d+\/P(\d+)[^)]*)\)/g,
      (_, url, para) => ' ' + createRISLinkHtml(url, `[\u00A7 ${para}]`, 'text-primary hover:text-accent-dark underline'))
    .replace(/\((https?:\/\/(?:www\.)?ris\.bka\.gv\.at\/eli\/[^)]+)\)/g,
      (_, url) => ' ' + createRISLinkHtml(url, '[Gesetz]', 'text-primary hover:text-accent-dark underline'))
    .replace(/\((https?:\/\/(?:www\.)?ris\.bka\.gv\.at[^)]+)\)/g,
      (_, url) => ' ' + createRISLinkHtml(url, '[RIS]', 'text-primary hover:text-accent-dark underline'))
    // BGBl references: "BGBl I 2025/111", "BGBl. II Nr. 50/2024", "BGBl I Nr. 111/2025"
    .replace(/BGBl\.?\s+(I{1,3})\s+(?:Nr\.?\s+)?(\d{1,4})\/(\d{4})/g,
      (match, teil, nummer, jahr) => {
        const teilMap: Record<string, string> = { 'I': '1', 'II': '2', 'III': '3' };
        const bgblTeil = teilMap[teil] || '1';
        const url = `https://www.ris.bka.gv.at/Ergebnis.wxe?Abfrage=BgblAuth&Bgblnummer=${encodeURIComponent(nummer + '/' + jahr)}&BgblTeil=${bgblTeil}`;
        return createRISLinkHtml(url, match);
      })
    // Dokumentmarken: angereichert (dokumentId:uuid|Titel|Adresse) mit dem Titel
    // als Beschriftung, kurz (dokumentId:uuid) mit der Ersatzbeschriftung.
    .replace(DOCUMENT_MARKER_PARTS,
      (_, docId: string, title: string | undefined, sourceUrl: string | undefined) => {
        if (title === undefined) {
          return `<a href="#" data-document-id="${docId}" class="text-primary hover:text-accent-dark underline cursor-pointer font-medium">[Dokument anzeigen]</a>`;
        }
        const linkText = escHtml(title.trim() || 'Dokument anzeigen');
        const viewerLink = `<a href="#" data-document-id="${docId}" class="text-primary hover:text-accent-dark underline cursor-pointer font-medium">${linkText}</a>`;
        const trimmedUrl = sourceUrl?.trim();
        if (trimmedUrl && /^https?:\/\//i.test(trimmedUrl)) {
          return `${viewerLink} <a href="${escHtml(trimmedUrl)}" target="_blank" rel="noopener noreferrer" class="text-gray-500 hover:text-gray-700 text-xs ml-1" title="WKO Quelle">[Quelle]</a>`;
        }
        return viewerLink;
      })
    // Fallback 2: bare dokumentId without parentheses (e.g. ", dokumentId: UUID")
    .replace(/(?:,\s*)?dokumentId:\s*([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/gi,
      (_, docId) => ` <a href="#" data-document-id="${docId}" class="text-primary hover:text-accent-dark underline cursor-pointer font-medium">[Dokument anzeigen]</a>`)
    // Other URLs
    .replace(/\[(https?:\/\/(?!(?:www\.)?ris\.bka\.gv\.at)[^\s\]]+)\]/g, ' <a href="$1" target="_blank" rel="noopener noreferrer" class="text-primary hover:text-accent-dark underline">[Link]</a>')
    .replace(/\[(https?:\/\/(?:www\.)?ris\.bka\.gv\.at[^\s\]]+)\]/g,
      (_, url) => ' ' + createRISLinkHtml(url, '[RIS]', 'text-primary hover:text-accent-dark underline'))
    // Convert Markdown links [text](url)
    .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
      (_, text, url) => createRISLinkHtml(url, text))
    // Convert remaining standalone URLs to clickable links
    .replace(/(^|[^">])(https?:\/\/[^\s<)]+)/g, (match, prefix, url) => {
      if (url.includes('ris.bka.gv.at')) {
        return prefix + createRISLinkHtml(url, url, 'text-primary hover:text-accent-dark underline break-all');
      }
      return `${prefix}<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-primary hover:text-accent-dark underline break-all">${url}</a>`;
    })
    // Reduce double line breaks after headers
    .replace(/(<\/h[1-6]>)\n\n/g, '$1\n')
    // Convert line breaks to HTML
    .replace(/\n/g, '<br>');

  // Step 3: Restore table HTML from placeholders (after \n -> <br> conversion)
  formatted = restoreTablePlaceholders(formatted, tables);

  // Step 4: Apply RIS Link hyperlinks if metadata is available
  let finalFormatted = formatted;

  if (risLinkMetadata) {
    // Convert Geschaeftszahlen to hyperlinks
    risLinkMetadata.geschaeftszahlen.forEach(({geschaeftszahl, risUrl}) => {
      const escapedGeschaeftszahl = geschaeftszahl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(?<!\\w)${escapedGeschaeftszahl}(?!\\w)`, 'g');
      finalFormatted = finalFormatted.replace(regex, (match, offset, fullStr) => {
        const before = fullStr.substring(Math.max(0, offset - 500), offset);
        const lastAOpen = before.lastIndexOf('<a ');
        const lastAClose = before.lastIndexOf('</a>');
        if (lastAOpen > lastAClose) return match;
        return createRISLinkHtml(risUrl, match);
      });
    });

    // Convert Paragraphen to hyperlinks
    risLinkMetadata.paragraphen.forEach(({paragraph, risLink}) => {
      const escapedParagraph = paragraph.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(?<!\\w)${escapedParagraph}(?!\\w)`, 'g');
      finalFormatted = finalFormatted.replace(regex, (match, offset, fullStr) => {
        const before = fullStr.substring(Math.max(0, offset - 500), offset);
        const lastAOpen = before.lastIndexOf('<a ');
        const lastAClose = before.lastIndexOf('</a>');
        if (lastAOpen > lastAClose) return match;
        return createRISLinkHtml(risLink, match);
      });
    });
  }

  return finalFormatted;
}

export function antwortRohHtml(content: string, risLinkMetadata?: RISLinkMetadata): string {
  return formatMessageHtml(content, new Map(), risLinkMetadata);
}

// Für dangerouslySetInnerHTML: sanitizeHtml bleibt der letzte Schritt vor der Seite.
export function antwortHtml(content: string, risLinkMetadata?: RISLinkMetadata): { __html: string } {
  return { __html: sanitizeHtml(antwortRohHtml(content, risLinkMetadata)) };
}

/** Dasselbe HTML, bereinigt, wie es die Nachricht im Chat mit ihren aufgelösten Dokumentadressen einsetzt. */
export function nachrichtHtml(
  content: string,
  resolvedDocuments: ResolvedDocumentMap,
  risLinkMetadata?: RISLinkMetadata,
): { __html: string } {
  return { __html: sanitizeHtml(formatMessageHtml(content, resolvedDocuments, risLinkMetadata)) };
}
